import React, { useMemo, useState } from "react";
import { Card, Button, Table } from "react-bootstrap";
import { ArrowLeft, ChevronDown } from "react-bootstrap-icons";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown } from "lucide-react";

const OrdersInsightDetails = () => {
    const navigate = useNavigate();
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);

    const [activeTab, setActiveTab] = useState("overall");
    const [groupBy, setGroupBy] = useState("Daily");
    const [dateRange, setDateRange] = useState("Last 30 Days");
    const [showGroupMenu, setShowGroupMenu] = useState(false);
    const [showRangeMenu, setShowRangeMenu] = useState(false);
    const [selectedDay, setSelectedDay] = useState("");
    const [tooltipData, setTooltipData] = useState(null);

    const dummyOrdersData = useMemo(() => {
        const today = new Date(2026, 6, 1);

        return Array.from({ length: 370 }, (_, index) => {
            const date = new Date(today);
            date.setDate(today.getDate() - (369 - index));

            const totalOrders = 850 + Math.floor(Math.random() * 650);
            const newCustomerOrders = Math.floor(totalOrders * 0.35);
            const repeatCustomerOrders = totalOrders - newCustomerOrders;

            return {
                date,
                totalOrders,
                newCustomerOrders,
                repeatCustomerOrders,
            };
        });
    }, []);

    const groupOptions = ["Daily", "Day based", "Weekly", "Monthly", "Quarterly", "Yearly"];
    const rangeOptions = ["Last 30 Days", "Last 90 Days", "Last 3 Months", "Last 6 Months", "Last 12 Months"];
    const dayOptions = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun", "Weekend", "Weekday"];

    const formatIndianNumber = (value) => Number(value || 0).toLocaleString("en-IN");

    const formatOrders = (value) => {
        const number = Number(value || 0);

        if (number >= 100000) {
            return `${(number / 100000).toFixed(2).replace(/\.00$/, "")} Lakh`;
        }

        return formatIndianNumber(number);
    };

    const formatDate = (date) =>
        new Intl.DateTimeFormat("en-IN", {
            day: "numeric",
            month: "short",
            year: "2-digit",
        })
            .format(date)
            .replace(",", "");

    const getRangeDays = () => {
        if (dateRange === "Last 90 Days") return 90;
        if (dateRange === "Last 3 Months") return 90;
        if (dateRange === "Last 6 Months") return 180;
        if (dateRange === "Last 12 Months") return 365;
        return 30;
    };

    const getPeriodKey = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const quarter = Math.floor(month / 3) + 1;

        if (groupBy === "Weekly") {
            const start = new Date(date);
            start.setDate(date.getDate() - date.getDay());
            return formatDate(start);
        }

        if (groupBy === "Monthly") {
            return new Intl.DateTimeFormat("en-IN", {
                month: "short",
                year: "numeric",
            }).format(date);
        }

        if (groupBy === "Quarterly") return `Q${quarter} ${year}`;
        if (groupBy === "Yearly") return `${year}`;

        return formatDate(date);
    };

    const filteredData = useMemo(() => {
        const rangeDays = getRangeDays();
        const endDate = dummyOrdersData[dummyOrdersData.length - 1]?.date || new Date();
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - rangeDays + 1);

        let data = dummyOrdersData.filter((item) => item.date >= startDate && item.date <= endDate);

        if (groupBy === "Day based" && selectedDay) {
            data = data.filter((item) => {
                const day = item.date.toLocaleDateString("en-US", { weekday: "short" });
                const isWeekend = ["Sat", "Sun"].includes(day);

                if (selectedDay === "Weekend") return isWeekend;
                if (selectedDay === "Weekday") return !isWeekend;

                return day === selectedDay;
            });
        }

        return data;
    }, [dummyOrdersData, dateRange, groupBy, selectedDay]);

    const chartData = useMemo(() => {
        if (groupBy === "Daily" || groupBy === "Day based") {
            return filteredData.map((item) => ({
                label: formatDate(item.date),
                dateLabel: formatDate(item.date),
                totalOrders: item.totalOrders,
                newCustomerOrders: item.newCustomerOrders,
                repeatCustomerOrders: item.repeatCustomerOrders,
                value:
                    activeTab === "new-repeat"
                        ? item.newCustomerOrders + item.repeatCustomerOrders
                        : item.totalOrders,
                count: 1,
            }));
        }

        const grouped = {};

        filteredData.forEach((item) => {
            const key = getPeriodKey(item.date);

            if (!grouped[key]) {
                grouped[key] = {
                    label: key,
                    dateLabel: key,
                    totalOrders: 0,
                    newCustomerOrders: 0,
                    repeatCustomerOrders: 0,
                    value: 0,
                    count: 0,
                };
            }

            grouped[key].totalOrders += item.totalOrders;
            grouped[key].newCustomerOrders += item.newCustomerOrders;
            grouped[key].repeatCustomerOrders += item.repeatCustomerOrders;
            grouped[key].value += item.totalOrders;
            grouped[key].count += 1;
        });

        return Object.values(grouped);
    }, [filteredData, groupBy, activeTab]);

    const totalOrders = useMemo(
        () => chartData.reduce((sum, item) => sum + item.value, 0),
        [chartData]
    );

    const averageOrders = chartData.length ? totalOrders / chartData.length : 0;
    const maxOrders = Math.max(...chartData.map((item) => item.value), 1);

    const chartMax = Math.ceil(maxOrders / 500) * 500 || 2000;
    const chartTicks = Array.from({ length: 6 }, (_, index) =>
        Math.round(chartMax - (chartMax / 5) * index)
    );

    const xAxisLabels = useMemo(() => {
        if (!chartData.length) return [];
        const step = Math.max(Math.floor(chartData.length / 4), 1);
        return chartData.filter((_, index) => index % step === 0).slice(0, 5);
    }, [chartData]);

    const periodLabel =
        groupBy === "Day based" && selectedDay
            ? `${selectedDay} in ${dateRange.toLowerCase()}`
            : dateRange.toLowerCase();

    const handleTooltipOpen = (event, item) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const tooltipWidth = 270;
        const windowWidth = window.innerWidth;

        let left = rect.left + rect.width / 2 - tooltipWidth / 2;

        if (left < 12) left = 12;
        if (left + tooltipWidth > windowWidth - 12) {
            left = windowWidth - tooltipWidth - 12;
        }

        setTooltipData({
            item,
            left,
            top: rect.top - 140,
        });
    };

    return (
        <>
            <Header onSkin={setSkin} />

            <div className="main main-app">
                <div style={pageWrapperStyle}>
                    <div style={topActionStyle}>
                        <Button
                            variant="light"
                            onClick={() => navigate("/dashboard")}
                            style={backButtonStyle}
                        >
                            <ArrowLeft size={18} /> Go Back
                        </Button>

                        <Button variant="light" style={exportButtonStyle}>
                            Export
                        </Button>
                    </div>

                    <h1 style={pageTitleStyle}>
                        How many <span style={{ color: "#1A73E8" }}>Orders</span> have been made in my business?
                    </h1>

                    <div className="orders-filter-card" style={filterCardStyle}>
                        <div style={leftTabsStyle}>
                            <button
                                onClick={() => setActiveTab("overall")}
                                style={activeTab === "overall" ? activeTabStyle : tabStyle}
                            >
                                Overall
                            </button>

                            <button
                                onClick={() => setActiveTab("new-repeat")}
                                style={activeTab === "new-repeat" ? activeTabStyle : tabStyle}
                            >
                                New vs Repeat
                            </button>
                        </div>

                        <div style={rightFilterStyle}>
                            <div style={dropdownWrapStyle}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowGroupMenu(!showGroupMenu);
                                        setShowRangeMenu(false);
                                    }}
                                    style={showGroupMenu ? activeFilterStyle : filterStyle}
                                >
                                    {groupBy === "Day based" && selectedDay ? selectedDay : groupBy}
                                    <ChevronDown size={18} />
                                </button>

                                {showGroupMenu && (
                                    <div style={menuStyle}>
                                        {groupOptions.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => {
                                                    setGroupBy(option);
                                                    if (option !== "Day based") setSelectedDay("");
                                                }}
                                                style={menuItemStyle}
                                            >
                                                {option}
                                            </button>
                                        ))}

                                        {groupBy === "Day based" && (
                                            <div style={dayPopupInnerStyle}>
                                                <div style={dayPopupTitleStyle}>Select Day</div>

                                                <div style={dayGridStyle}>
                                                    {dayOptions.map((day) => (
                                                        <button
                                                            key={day}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedDay(day);
                                                                setShowGroupMenu(false);
                                                            }}
                                                            style={selectedDay === day ? dayBtnActiveStyle : dayBtnStyle}
                                                        >
                                                            {day}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={dropdownWrapStyle}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRangeMenu(!showRangeMenu);
                                        setShowGroupMenu(false);
                                    }}
                                    style={filterStyle}
                                >
                                    {dateRange}
                                    <ChevronDown size={18} />
                                </button>

                                {showRangeMenu && (
                                    <div style={menuStyle}>
                                        {rangeOptions.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => {
                                                    setDateRange(option);
                                                    setShowRangeMenu(false);
                                                }}
                                                style={menuItemStyle}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <Card style={mainCardStyle}>
                        <Card.Body style={{ padding: "28px 28px 24px" }}>
                            <h2 style={amountStyle}>
                                {formatOrders(totalOrders)}
                                <span style={avgTextStyle}>
                                    {" "}({formatOrders(Math.round(averageOrders))} avg)
                                </span>
                            </h2>

                            <p style={subTextStyle}>Orders in {periodLabel}</p>

                            <div style={chartShellStyle}>
                                <div style={chartOuterStyle}>
                                    <div style={yAxisStyle}>
                                        {chartTicks.map((tick) => (
                                            <span key={tick}>{formatOrders(tick)}</span>
                                        ))}
                                    </div>

                                    <div style={chartAreaStyle}>
                                        <div style={barsWrapStyle}>
                                            {chartData.map((item, index) => {
                                                const height = Math.max((item.value / chartMax) * 100, 5);
                                                const showValue =
                                                    chartData.length <= 12 ||
                                                    index % Math.ceil(chartData.length / 8) === 0;

                                                return (
                                                    <div
                                                        key={`${item.label}-${index}`}
                                                        style={barItemStyle}
                                                        onMouseEnter={(event) => handleTooltipOpen(event, item)}
                                                        onMouseLeave={() => setTooltipData(null)}
                                                    >
                                                        {showValue && (
                                                            <div style={{ ...barValueStyle, bottom: `${height + 3}%` }}>
                                                                {formatOrders(item.value)}
                                                            </div>
                                                        )}

                                                        <div style={{ ...barStyle, height: `${height}%` }} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div style={xAxisStyle}>
                                    {xAxisLabels.map((item, index) => (
                                        <span key={`${item.label}-${index}`}>{item.label}</span>
                                    ))}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <div style={{ marginTop: 34 }}>
                        <Table borderless responsive style={{ marginBottom: 0 }}>
                            <thead>
                                <tr>
                                    <th style={tableHeadStyle}>Time Period</th>
                                    <th style={tableHeadStyle}>
                                        Total Orders <ArrowUpDown size={14} />
                                    </th>
                                    <th style={tableHeadStyle}>
                                        New Customer Orders <ArrowUpDown size={14} />
                                    </th>
                                    <th style={tableHeadStyle}>
                                        Repeat Customer Orders <ArrowUpDown size={14} />
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {chartData.map((item, index) => (
                                    <tr key={`${item.label}-${index}`}>
                                        <td style={tableCellStyle}>{item.label}</td>
                                        <td style={tableCellStyle}>{formatOrders(item.totalOrders)}</td>
                                        <td style={tableCellStyle}>{formatOrders(item.newCustomerOrders)}</td>
                                        <td style={tableCellStyle}>{formatOrders(item.repeatCustomerOrders)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            {tooltipData && (
                <div
                    style={{
                        ...tooltipStyle,
                        left: tooltipData.left,
                        top: tooltipData.top < 12 ? 12 : tooltipData.top,
                    }}
                >
                    <strong style={tooltipTitleStyle}>{tooltipData.item.dateLabel}</strong>
                    <span>Total Orders: {formatOrders(tooltipData.item.totalOrders)}</span>
                    <span>New Customer Orders: {formatOrders(tooltipData.item.newCustomerOrders)}</span>
                    <span>Repeat Customer Orders: {formatOrders(tooltipData.item.repeatCustomerOrders)}</span>
                    <span>Days Count: {tooltipData.item.count}</span>
                </div>
            )}

            <div className="mt-4">
                <Footer />
            </div>

            <style>
                {`
                    tbody tr {
                        background: #F4FAFF;
                        border-bottom: 14px solid #fff;
                    }

                    tbody td {
                        padding: 18px 22px !important;
                        font-weight: 600;
                        color: #344244;
                    }

                    tbody tr td:first-child {
                        border-radius: 14px 0 0 14px;
                    }

                    tbody tr td:last-child {
                        border-radius: 0 14px 14px 0;
                    }

                    @media (max-width: 992px) {
                        .orders-filter-card {
                            flex-direction: column;
                            align-items: flex-start !important;
                        }
                    }
                `}
            </style>
        </>
    );
};

const pageWrapperStyle = {
    background: "#ffffff",
    minHeight: "100vh",
    padding: 28,
};

const topActionStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 22,
};

const backButtonStyle = {
    border: "1px solid #d8dee4",
    borderRadius: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
};

const exportButtonStyle = {
    border: "1px solid #d8dee4",
    borderRadius: 10,
    fontWeight: 700,
    background: "#fff",
    padding: "11px 18px",
};

const pageTitleStyle = {
    fontSize: 34,
    fontWeight: 700,
    color: "#071C1F",
    marginBottom: 26,
};

const filterCardStyle = {
    background: "#EEF8FF",
    borderRadius: 16,
    padding: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 18,
    marginBottom: 26,
};

const leftTabsStyle = {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
};

const tabStyle = {
    border: "1px solid #d8dee4",
    background: "#fff",
    color: "#17292b",
    padding: "11px 16px",
    borderRadius: 8,
    fontWeight: 700,
};

const activeTabStyle = {
    ...tabStyle,
    background: "#1A73E8",
    borderColor: "#1A73E8",
    color: "#fff",
};

const rightFilterStyle = {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
};

const dropdownWrapStyle = {
    position: "relative",
};

const filterStyle = {
    border: "1px solid #d8dee4",
    borderRadius: 10,
    fontWeight: 600,
    color: "#17292b",
    padding: "11px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 45,
    background: "#fff",
    minWidth: 150,
};

const activeFilterStyle = {
    ...filterStyle,
    borderColor: "#1A73E8",
    boxShadow: "0 0 0 1px #1A73E8",
};

const menuStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    background: "#fff",
    border: "1px solid #d8dee4",
    borderRadius: 10,
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
    minWidth: 180,
    padding: "8px 0",
    zIndex: 50,
};

const menuItemStyle = {
    width: "100%",
    border: 0,
    background: "#fff",
    textAlign: "left",
    padding: "10px 16px",
    fontWeight: 500,
    color: "#17292b",
};

const dayPopupInnerStyle = {
    borderTop: "1px solid #edf1f2",
    marginTop: 6,
    padding: "12px 14px 8px",
    width: 300,
};

const dayPopupTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#687779",
    marginBottom: 12,
};

const dayGridStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
};

const dayBtnStyle = {
    border: "1px solid #cfd8da",
    background: "#fff",
    color: "#17292b",
    borderRadius: 8,
    padding: "8px 14px",
    fontWeight: 700,
};

const dayBtnActiveStyle = {
    ...dayBtnStyle,
    borderColor: "#1A73E8",
    color: "#1A73E8",
};

const mainCardStyle = {
    border: "1px solid #dce5e7",
    borderRadius: 16,
    boxShadow: "none",
};

const amountStyle = {
    fontSize: 42,
    fontWeight: 700,
    color: "#071C1F",
    marginBottom: 14,
};

const avgTextStyle = {
    fontSize: 30,
    color: "#6f7f82",
    fontWeight: 500,
};

const subTextStyle = {
    fontSize: 17,
    fontWeight: 600,
    color: "#071C1F",
    marginBottom: 20,
};

const chartShellStyle = {
    width: 790,
    maxWidth: "100%",
    marginLeft: 0,
};

const chartOuterStyle = {
    display: "grid",
    gridTemplateColumns: "58px 1fr",
    alignItems: "stretch",
    height: 275,
};

const yAxisStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#7b8794",
    padding: "0 10px 14px 0",
    textAlign: "right",
};

const chartAreaStyle = {
    position: "relative",
    height: 275,
    overflow: "visible",
};

const barsWrapStyle = {
    position: "absolute",
    inset: "0 0 14px 0",
    display: "flex",
    alignItems: "flex-end",
    gap: 9,
    overflowX: "auto",
    overflowY: "visible",
    paddingTop: 28,
};

const barItemStyle = {
    position: "relative",
    width: 15,
    height: "100%",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    flex: "0 0 15px",
};

const barStyle = {
    width: 15,
    background: "#1A73E8",
    borderRadius: "4px 4px 0 0",
    transition: "0.25s ease",
};

const barValueStyle = {
    position: "absolute",
    fontSize: 12,
    fontWeight: 700,
    color: "#17292b",
    whiteSpace: "nowrap",
};

const tooltipStyle = {
    position: "fixed",
    width: 270,
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "12px 14px",
    boxShadow: "0 12px 35px rgba(0,0,0,0.18)",
    zIndex: 999999,
    display: "flex",
    flexDirection: "column",
    gap: 5,
    fontSize: 12,
    color: "#344244",
    pointerEvents: "none",
};

const tooltipTitleStyle = {
    color: "#071C1F",
    fontSize: 13,
    marginBottom: 2,
};

const xAxisStyle = {
    marginLeft: 58,
    display: "flex",
    justifyContent: "space-between",
    color: "#7b8794",
    fontSize: 12,
    marginTop: 8,
};

const tableHeadStyle = {
    fontSize: 17,
    color: "#344244",
    fontWeight: 800,
    padding: "0 22px 16px",
    whiteSpace: "nowrap",
    textAlign: "center",
};

const tableCellStyle = {
    fontSize: 16,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    textAlign: "center",
};

export default OrdersInsightDetails;